import { useState } from 'react';
import axios from 'axios';
import useUser from '../hooks/useUser';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';

const TextToCogoTool = () => {
  const [inputText, setInputText] = useState('');
  const [resultText, setResultText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useUser();

  const runAnalysis = async () => {
    if (!inputText.trim()) {
      alert('Please enter text before running analysis!');
      return;
    }

    setLoading(true);
    setError('');
    setResultText('');

    try {
      const response = await axios.post(
        'https://cogo-genie-stg-254627229595.us-west1.run.app/api/analyze',
        { input_text: inputText }
      );

      const data = response.data;

      if (data.results) {
        const header = 'DT QB\nDU DMS\nSP 00000.00 00000.00\n\n';
        const body =
          Array.isArray(data.results) ? data.results.join('\n') : data.results;
        setResultText(header + body);
      } else if (data.error) {
        setResultText(`Error: ${data.error}`);
      }
    } catch (err) {
      setError('An error occurred during analysis.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = () => {
    if (!resultText.trim()) {
      alert('No results to download!');
      return;
    }

    const blob = new Blob([resultText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'results.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box
      sx={{
        mt: 10,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#4B006E',
        minHeight: '100vh',
        py: 5,
        px: 2,
      }}
    >
      {/* Banner */}
      <Box
        component="img"
        src="https://i.imgur.com/YOChZFg.jpeg"
        alt="COGO Genie"
        sx={{
          maxWidth: '40%',
          mb: 3,
          cursor: 'pointer',
        }}
        onClick={() => window.open('https://cogogenie.com', '_blank')}
      />

      <Typography variant="h4" sx={{ color: '#b7a57a', mb: 1 }}>
        Text to COGO Tool
      </Typography>

      <Typography sx={{ color: '#b7a57a', mb: 4, textAlign: 'center', maxWidth: 600 }}>
        Easily convert legal land descriptions into ArcGIS-compatible traverse
        instructions using our machine learning tool.
      </Typography>

      <Paper
        sx={{
          backgroundColor: '#EAE0D5',
          p: 3,
          width: '100%',
          maxWidth: 600,
          borderRadius: 2,
        }}
        elevation={3}
      >
        {/* Input */}
        <Typography variant="subtitle1" sx={{ color: '#4B006E', mb: 1 }}>
          Insert text here:
        </Typography>
        <TextField
          multiline
          rows={10}
          fullWidth
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Enter your data..."
          sx={{
            backgroundColor: 'white',
            borderRadius: 1,
            mb: 2,
          }}
        />

        {/* Submit Button */}
        <Button
          onClick={runAnalysis}
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#4B006E',
            color: '#b7a57a',
            fontWeight: 'bold',
            mb: 2,
            '&:hover': {
              backgroundColor: '#0A0908',
              color: '#fff',
            },
          }}
        >
          {loading ? <CircularProgress size={24} sx={{ color: '#b7a57a' }} /> : '?? Grant my wish ??'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Output */}
        <Typography variant="subtitle1" sx={{ color: '#4B006E', mb: 1 }}>
          Results:
        </Typography>
        <TextField
          multiline
          rows={10}
          fullWidth
          value={resultText}
          InputProps={{ readOnly: true }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 1,
            mb: 2,
          }}
        />

        {/* Download Button */}
        <Button
          fullWidth
          onClick={downloadFile}
          sx={{
            backgroundColor: '#b7a57a',
            color: '#4B006E',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#0A0908',
              color: '#fff',
            },
          }}
        >
          Download Text File
        </Button>
      </Paper>
    </Box>
  );
};

export default TextToCogoTool;
